import {
  IonCard,
  IonHeader,
  IonIcon,
  IonInfiniteScroll,
  IonInfiniteScrollContent,
  IonList,
  IonPage,
  IonRadio,
  IonRadioGroup,
  IonRefresher,
  IonRefresherContent,
  IonTitle,
  IonToolbar,
  type RefresherEventDetail,
  useIonRouter,
  useIonViewWillEnter,
} from "@ionic/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Button } from "@/components/button";
import { Buttons } from "@/components/buttons";
import { Content } from "@/components/content";
import { ModalSheetAutoHeight } from "@/components/modal/sheet/auto-height";
import { NumberTicker } from "@/components/molecules/magicui/number-ticker";
import { ShiftCardCompleted } from "@/components/shift/card/completed";
import { ShiftCardToDeclare } from "@/components/shift/card/to-declare";
import { Toolbar } from "@/components/toolbar";
import { api } from "@/lib/backend";
import { extractionWrapper } from "@/lib/backend/utils/extract";
import { useExtractedQuery } from "@/lib/backend/utils/hooks";
import { useMe } from "@/lib/hooks/use-me";
import { chevronDownIcon } from "@/lib/icons/chevron-down.icon";
import { crossIcon } from "@/lib/icons/cross.icon";
import { PERIOD_SELECT_OPTIONS } from "@/lib/period-select";
import { Routes } from "@/lib/router";
import { calculateShiftDuration } from "@/utils/time";

const currentDate = new Date();
export function CompletedShiftsPage() {
  const { t } = useTranslation();
  const router = useIonRouter();
  const { period } = Routes.CompletedShifts.useSearchParams();

  const modalDateSelect = useRef<HTMLIonModalElement>(null);

  const { me } = useMe();

  const selectedPeriod = PERIOD_SELECT_OPTIONS.find((option) => option.value === period)!.filter(
    currentDate,
  );

  const ITEMS_PER_PAGE = 10;

  const { data: statistics, refetch: refetchStatistics } = useExtractedQuery({
    queryKey: [
      "shifts",
      "stats",
      {
        filter: {
          employeeId: { operator: "equals", value: me?.employee.id },
          startAt: { operator: "gte", value: selectedPeriod.from.toISOString() },
          endAt: { operator: "lte", value: selectedPeriod.to.toISOString() },
        },
        pagination: { take: 9999, page: 1 },
        include: {
          project: {
            select: true,
          },
          shiftTimeEntry: {
            select: true,
          },
        },
      } satisfies Parameters<typeof api.shift.getAll>[0],
    ] as const,
    queryFn: (context) => api.shift.getAll(context.queryKey[2], { signal: context.signal }),
    staleTime: Infinity,
    select: (data) => {
      return data.items?.reduce(
        (acc, shift) => {
          acc.totalAmount += 1;

          const time = calculateShiftDuration(
            shift.shiftTimeEntry?.startAt ?? shift.startAt,
            shift.shiftTimeEntry?.endAt ?? shift.endAt,
            shift.shiftTimeEntry?.breakTimeMinutes ?? 0,
          );

          // Add minutes to accumulator
          acc.totalMinutes += time.minutes;

          // Add hours from the current shift
          acc.totalHours += time.hours;

          // Handle minute overflow
          if (acc.totalMinutes >= 60) {
            acc.totalHours += Math.floor(acc.totalMinutes / 60);
            acc.totalMinutes = acc.totalMinutes % 60;
          }

          return acc;
        },
        { totalAmount: 0, totalHours: 0, totalMinutes: 0 },
      );
    },
  });

  const { data, fetchNextPage, hasNextPage, refetch } = useInfiniteQuery({
    queryKey: [
      "shifts",
      {
        filter: {
          startAt: { operator: "gte", value: selectedPeriod.from.toISOString() },
          endAt: { operator: "lte", value: selectedPeriod.to.toISOString() },
          employeeId: { operator: "equals", value: me?.employee.id },
        },
        sort: { field: "startAt", order: "desc" },
        pagination: { take: ITEMS_PER_PAGE },
      } satisfies Parameters<typeof api.shift.getAll>[0],
    ] as const,
    queryFn: (context) => {
      return extractionWrapper(
        api.shift.getAll(
          {
            ...context.queryKey[1],
            pagination: {
              ...context.queryKey[1].pagination,
              page: context.pageParam + 1,
            },
            include: {
              project: {
                select: true,
              },
              shiftTimeEntry: {
                select: true,
              },
            },
          },
          { signal: context.signal },
        ),
      );
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) => {
      const totalItems = lastPage.totalAmount;
      const currentItems = (lastPageParam + 1) * ITEMS_PER_PAGE;

      if (currentItems >= totalItems) {
        return undefined;
      }

      return lastPageParam + 1;
    },
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    refetchOnReconnect: true,
    getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
      return firstPageParam - 1;
    },
    select: (data) => data.pages.flatMap((page) => page.items).filter(Boolean),
  });

  const shiftsToDeclare = data?.filter(
    (shift) =>
      (!shift.shiftTimeEntry?.id || // No shift time entry
        !shift.shiftTimeEntry?.startAt || // No start time
        !shift.shiftTimeEntry?.endAt || // No end time
        shift.shiftTimeEntry?.breakTimeMinutes === null) && // No break time
      shift.project?.allowEmployeeSubmitHours &&
      shift.project?.status !== "FINALIZED",
  );

  const shiftsCompleted = data?.filter(
    (shift) =>
      !!shift.shiftTimeEntry?.id ||
      !shift.project?.allowEmployeeSubmitHours ||
      shift.project?.status === "FINALIZED",
  );

  const loadMore = async (ev: CustomEvent) => {
    await fetchNextPage();
    if (ev.target) {
      (ev.target as any).complete();
    }
  };

  useIonViewWillEnter(() => {
    refetchStatistics();
    refetch();
  });

  const handleRefresh = async (event: CustomEvent<RefresherEventDetail>) => {
    await Promise.all([refetchStatistics(), refetch()]);
    event.detail.complete();
  };

  return (
    <IonPage>
      <IonHeader>
        <Toolbar>
          <IonTitle>{t("routes:completed-shifts.title")}</IonTitle>
          <Buttons slot="end" collapse>
            <Button
              color="primary"
              fill="outline"
              className="text-sm !ion-rounded-lg !ion-px-3 !ion-py-2"
              onClick={() => modalDateSelect.current?.present()}
            >
              {t(PERIOD_SELECT_OPTIONS.find((option) => option.value === period)!.label)}
              <IonIcon slot="end" icon={chevronDownIcon} />
            </Button>
          </Buttons>
        </Toolbar>
      </IonHeader>
      <Content className="part-scroll:gap-6">
        <IonRefresher slot="fixed" onIonRefresh={handleRefresh}>
          <IonRefresherContent refreshingSpinner="circles" />
        </IonRefresher>
        <IonHeader collapse="condense" className="-mt-7">
          <Toolbar className="!ion-p-0">
            <IonTitle className="p-0 text-start text-2xl">
              {t("routes:completed-shifts.title")}
            </IonTitle>
            <Buttons slot="end" collapse>
              <Button
                color="primary"
                fill="outline"
                className="text-sm !ion-rounded-lg !ion-px-3 !ion-py-2"
                onClick={() => modalDateSelect.current?.present()}
              >
                {t(PERIOD_SELECT_OPTIONS.find((option) => option.value === period)!.label)}
                <IonIcon slot="end" icon={chevronDownIcon} />
              </Button>
            </Buttons>
          </Toolbar>
        </IonHeader>
        <div className="-mt-6 flex gap-4 *:flex-1">
          <IonCard className="flex flex-col items-center justify-center px-4 py-8">
            <NumberTicker className="text-3xl font-bold" value={statistics?.totalAmount ?? 0} />
            <span className="text-lg font-medium text-txt-secondary">
              {t("routes:completed-shifts.statistics.shifts")}
            </span>
          </IonCard>
          <IonCard className="flex flex-col items-center justify-center px-4 py-8">
            <NumberTicker className="text-3xl font-bold" value={statistics?.totalHours ?? 0} />
            <span className="text-lg font-medium text-txt-secondary">
              {t("routes:completed-shifts.statistics.hours")}
            </span>
          </IonCard>
        </div>
        {!!shiftsToDeclare?.length && (
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-bold">{t("routes:completed-shifts.action-list")}</h2>
            <IonList className="flex flex-col gap-3">
              {shiftsToDeclare?.map((shift) => (
                <ShiftCardToDeclare
                  key={shift.id}
                  shift={shift}
                  clockType={shift.project?.enableQRCodeClocking ? "QR" : "MANUAL"}
                />
              ))}
            </IonList>
          </div>
        )}
        {!!shiftsCompleted?.length && (
          <div className="flex flex-col gap-3">
            <h2 className="text-xl font-bold">{t("routes:completed-shifts.latest-shifts")}</h2>
            <IonList className="flex flex-col gap-3">
              {shiftsCompleted?.map((shift) => <ShiftCardCompleted key={shift.id} shift={shift} />)}
            </IonList>
          </div>
        )}
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage}>
          <IonInfiniteScrollContent loadingText={t("common:loading")} loadingSpinner="bubbles" />
        </IonInfiniteScroll>
      </Content>

      <ModalSheetAutoHeight ref={modalDateSelect}>
        <IonHeader>
          <IonToolbar>
            <Buttons slot="start">
              <Button color="primary" onClick={() => modalDateSelect.current?.dismiss()}>
                <IonIcon slot="icon-only" icon={crossIcon} className="size-6" />
              </Button>
            </Buttons>
            <IonTitle>{t("modals:period-select.title")}</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonRadioGroup
          className="ion-padding flex flex-col gap-8"
          value={period}
          onIonChange={(event) =>
            router.push(
              Routes.CompletedShifts(undefined, { search: { period: event.detail.value } }),
              "none",
              "replace",
            )
          }
        >
          {PERIOD_SELECT_OPTIONS.map((period) => (
            <IonRadio key={period.value} value={period.value}>
              {t(period.label)}
            </IonRadio>
          ))}
        </IonRadioGroup>
      </ModalSheetAutoHeight>
    </IonPage>
  );
}
